import React from 'react';

import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
	ChartWrapper,
	TooltipWrapper,
	TooltipHeader,
	TooltipRowTitle,
	TooltipRowValue,
	TooltipGrid,
	ColorWrapper,
	ColorSymbol,
	TooltipRowPinned,
} from './CommonVendorsChart.styles';
import { colors } from '@alixpartners/ui-components';
import { renderToString } from 'react-dom/server';

export const CommonVendorsChart = ({
	chartRef,
	spendFormatter,
	series,
	categories,
}) => {
	let timeoutRef = null;
	const chart = chartRef.current && chartRef.current.chart;
	const chartOptions = {
		chart: {
			type: 'column',
			marginTop: 40,
			style: { fontFamily: 'Roboto' },
		},
		title: {
			text: '',
		},
		exporting: { enabled: false, allowHTML: true },
		credits: {
			enabled: false,
		},
		xAxis: {
			title: {
				enabled: false,
			},
			lineColor: 'black',
			categories,
			labels: {
				style: {
					fontSize: '11px',
					fontWeight: '400',
					lineHeight: '13px',
					letterSpacing: 0,
					color: colors.grey8,
				},
			},
			gridLineWidth: 0,
		},
		yAxis: {
			title: {
				align: 'high',
				text: 'Spend',
				textAlign: 'right',
				rotation: 0,
				y: -30,
				offset: 15,
				style: {
					fontSize: '11px',
					fontWeight: '400',
					lineHeight: '13px',
					letterSpacing: 0,
					color: colors.grey8,
				},
			},
			gridLineWidth: 0,
			labels: {
				formatter: function() {
					return spendFormatter(this.value, true);
				},
				style: {
					fontSize: '11px',
					fontWeight: '400',
					lineHeight: '13px',
					letterSpacing: 0,
					color: colors.grey8,
				},
			},
			lineColor: 'black',
			lineWidth: 1,
		},
		legend: {
			enabled: true,
			symbolRadius: 1,
			symbolHeight: 16,
			symbolPadding: 8,
			itemDistance: 32,
			useHTML: true,
			itemStyle: {
				color: colors.grey8,
				cursor: 'pointer',
				fontSize: '11px',
				fontWeight: 'normal',
				textOverflow: 'ellipsis',
				lineHeight: '11px',
			},
		},
		tooltip: {
			shared: true,
			useHTML: true,
			backgroundColor: colors.white,
			borderWidth: 0,
			outside: true,
			borderRadius: 2,
			formatter: function() {
				const { points, x } = this;
				const total = points && points.reduce((acc, point) => acc + point.y, 0);

				return renderToString(
					<TooltipWrapper>
						<TooltipHeader>{x}</TooltipHeader>
						<TooltipRowPinned>
							<div />
							<TooltipRowTitle>Total spend</TooltipRowTitle>
							<TooltipRowValue>{spendFormatter(total)}</TooltipRowValue>
						</TooltipRowPinned>
						<TooltipGrid>
							{points.map(pointData => (
								<React.Fragment key={pointData.point.company}>
									<ColorWrapper>
										<ColorSymbol color={pointData.color} />
									</ColorWrapper>
									<TooltipRowTitle>{pointData.point.company}</TooltipRowTitle>
									<TooltipRowValue>
										{pointData.y
											? `${spendFormatter(pointData.y)} (${Math.round(
													(pointData.y / total) * 100
											  )}%)`
											: '-'}
										{}
									</TooltipRowValue>
								</React.Fragment>
							))}
						</TooltipGrid>
					</TooltipWrapper>
				);
			},
		},
		plotOptions: {
			column: {
				groupPadding: 0.3,
			},
			series: {
				borderWidth: 0,
				pointPadding: 0,
				stacking: 'normal',
				dataLabels: {
					enabled: true,
					className: 'bar-label',
					style: {
						fontSize: '11px',
						fontWeight: '500',
					},
					color: 'black',
					overflow: 'none',
					crop: false,
					inside: false,
					position: 'top',
					allowOverlap: true,
					useHTML: true,
					formatter: function() {
						const formattedPrice = spendFormatter(this.y);
						const color = this.point.color;

						return `<span style="color:${color}; text-shadow: -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff;">${formattedPrice}</span>`;
					},
				},
				states: {
					hover: {
						enabled: false,
					},
				},
				events: {
					legendItemClick: function() {
						return false;
					},
					afterAnimate: function() {
						if (!chartRef.current || !chartRef.current.chart) {
							return;
						}

						clearTimeout(timeoutRef);
						timeoutRef = setTimeout(() => {
							if (!chart || !chart.container) {
								return;
							}
							applyWaterfallLabelsFixes({ chart, series });
						}, 1000);
					},
				},
			},
		},
		series,
	};

	const onChartInit = chart => {
		applyWaterfallLabelsFixes({ chart, series });
	};

	return (
		<ChartWrapper>
			<HighchartsReact
				highcharts={Highcharts}
				options={chartOptions}
				ref={chartRef}
				callback={onChartInit}
			/>
		</ChartWrapper>
	);
};

const STACKED_LABEL_HEIGHT = 14;
const applyWaterfallLabelsFixes = ({ chart, series: dataSeries }) => {
	if (!chart || !chart.series) {
		return;
	}

	const yLabelsPositions = [];
	const seriesLength = chart.series.length;
	// position of the labels

	chart.series.forEach((series, i) => {
		// onAnimate triggers multiple times, sometimes with series.points as null
		if (!series.points) {
			return;
		}
		series.points.forEach((point, j) => {
			const dataLabel = point.dataLabel;

			if (!dataLabel) return;

			const dataValue = getDataValueByIndex(dataSeries, i, j);
			const isLastSeries = i === chart.series.length - 1;

			if (!yLabelsPositions[j] && (dataValue > 0 || isLastSeries)) {
				yLabelsPositions[j] = dataLabel.y;
			}
		});
	});

	chart.series.forEach((series, i) => {
		// onAnimate triggers multiple times, sometimes with series.points as null
		if (!series.points) {
			return;
		}
		series.points.forEach((point, j) => {
			const dataLabel = point.dataLabel;
			if (!dataLabel) return;

			const y =
				yLabelsPositions[j] - STACKED_LABEL_HEIGHT * (seriesLength - 1 - i);

			dataLabel.translate(dataLabel.x, y);
		});
	});
};

const getDataValueByIndex = (dataSeries, i, j) => {
	const series = dataSeries[i];
	let dataValue = 0;

	if (series && series.data) {
		dataValue = series.data[j];

		// check if dataValue includes pattern together with value
		if (dataValue && (dataValue.value || dataValue.value === 0)) {
			dataValue = dataValue.value;
		}
	}

	return dataValue;
};

export default CommonVendorsChart;
